import omit from 'lodash/omit';

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import {
  getFrequencyDataSetLength,
  isRelativeFrequencyTable,
  getFilteredDatasetLength,
  getVisibleSelectedParameters,
  getCategoricalSummary
} from 'store/dataset';

import { getActiveChartsAndGraphsTab } from 'store/scenes/graphicalStatistics/rightPane/selectors';
import { getTypeOfFrequencyBarChart, getTypeOfFrequencyHistogram, getTypeOfFrequencyPieChart } from 'store/scenes/graphicalStatistics/settingsPane/selectors';
import { setTypeFrequencyPieChart, setTypeFrequencyBarChart, setTypeFrequencyHistogram } from 'store/scenes/graphicalStatistics/settingsPane/actions';

import SettingsFormComponent from './Component';

const SettingsFormContainer = props => {
  const {
    numericalName,
    firstParameterName,
    firstParameterSelected,
    firstParameterType,
    secondParameterName,
    secondParameterType,
    onFirstParameterNameChange,
    onSecondParameterNameChange,
    isFrequencyTable,
    frequencyParameters,
    visibleParameters,
    datasetLength,
    activeChartsAndGraphsTab,
    frequencyDataSetLength,
    isRelativeFrequency
  } = props;

  const [typeOfFrequencyHistogram, setTypeOfFrequencyHistogram] = useState(props.typeOfFrequencyHistogram);
  const [typeOfFrequencyBarChart, setTypeOfFrequencyBarChart] = useState(props.typeOfFrequencyBarChart);
  const [typeOfFrequencyPieChart, setTypeOfFrequencyPieChart] = useState(props.typeOfFrequencyPieChart);

  const visibleSelectedParameters = isFrequencyTable ? frequencyParameters : visibleParameters;
  const isDatasetChosen = datasetLength > 0;
  const firstDropdownParameters = visibleSelectedParameters;

  const secondDropdownParameters = omit(
    visibleSelectedParameters,
    firstParameterName
  );

  const firstDropdownValue =
    isDatasetChosen
      ? (firstParameterName || '')
      : '';

  const secondDropdownValue =
    isDatasetChosen
      ? (secondParameterName || '')
      : '';

  const hideSecondFieldForBoxPlotPieChartProbabilityPlot =
    activeChartsAndGraphsTab !== 2 &&
    activeChartsAndGraphsTab !== 5 &&
    activeChartsAndGraphsTab !== 6;

  const showSecondDropDown = () => {
    if (isFrequencyTable && frequencyDataSetLength === 1) {
      return hideSecondFieldForBoxPlotPieChartProbabilityPlot && activeChartsAndGraphsTab !== 4;
    }
    return hideSecondFieldForBoxPlotPieChartProbabilityPlot;
  };

  const showHistogramFields = props.showHistogramFields && isDatasetChosen;

  const getEachDropdownDisabled =
    isFrequencyTable && (activeChartsAndGraphsTab <= 3 || activeChartsAndGraphsTab === 6);

  const showRadioFrequency = () => {
    // for frequency table: show radio buttons only for  Bar Chart and Pie Chart
    // don't show for Relative Frequency Table
    if (isFrequencyTable && !isRelativeFrequency) {
      if (activeChartsAndGraphsTab === 4) {
        return (!secondDropdownValue && firstParameterType === 'categorical') ||
        (!!secondDropdownValue && secondParameterType === 'categorical' && firstParameterType === 'categorical');
      }

      if (activeChartsAndGraphsTab === 5) {
        return firstParameterType === 'categorical';
      }
    } else if (!isRelativeFrequency) {
      // for regular table: show radio buttons only for Histogram, Bar Chart and Pie Chart
      if (activeChartsAndGraphsTab === 0) {
        return showHistogramFields;
      }

      if (activeChartsAndGraphsTab === 4) {
        return (!secondDropdownValue && firstParameterType === 'categorical') ||
        (!!secondDropdownValue && secondParameterType === 'categorical' && firstParameterType === 'categorical');
      }

      if (activeChartsAndGraphsTab === 5) {
        return firstParameterType === 'categorical';
      }
    }
    return false;
  };

  const typeOfFrequency = () => {
    if (activeChartsAndGraphsTab === 0) {
      return typeOfFrequencyHistogram;
    }
    if (activeChartsAndGraphsTab === 4) {
      return typeOfFrequencyBarChart;
    }
    return typeOfFrequencyPieChart;
  };

  const handleFirstDropdownChange = parameterName => {
    onSecondParameterNameChange('');
    onFirstParameterNameChange(parameterName);
  };

  const handleSecondDropdownChange = parameterName =>
    onSecondParameterNameChange(parameterName);

  const handleFrequencyRadioButton = e => {
    if (activeChartsAndGraphsTab === 0) {
      setTypeOfFrequencyHistogram(e.target.value);
      return;
    }
    if (activeChartsAndGraphsTab === 4) {
      setTypeOfFrequencyBarChart(e.target.value);
      return;
    }
    setTypeOfFrequencyPieChart(e.target.value);
  };

  const handleRecalculateGraph = () => {
    if (activeChartsAndGraphsTab === 0) {
      props.setTypeFrequencyHistogram(typeOfFrequencyHistogram);
      return;
    }
    if (activeChartsAndGraphsTab === 4) {
      props.setTypeFrequencyBarChart(typeOfFrequencyBarChart);
      return;
    }
    props.setTypeFrequencyPieChart(typeOfFrequencyPieChart);
  };

  return (
    <SettingsFormComponent
      numericalName={numericalName}
      showHistogramFields={showHistogramFields}
      showRadioFrequency={showRadioFrequency()}
      // Dropdowns should be disabled for the first four pages in frequency table mode
      isEachDropdownDisabled={getEachDropdownDisabled}
      firstDropdownValue={firstDropdownValue}
      secondDropdownValue={secondDropdownValue}
      datasetLength={datasetLength}
      firstDropdownParameters={firstDropdownParameters}
      secondDropdownParameters={secondDropdownParameters}
      onFirstDropdownChange={handleFirstDropdownChange}
      onSecondDropdownChange={handleSecondDropdownChange}
      firstParameterSelected={firstParameterSelected}
      showSecondDropDown={showSecondDropDown()}
      onFrequencyRadioButton={handleFrequencyRadioButton}
      onRecalculateGraph={handleRecalculateGraph}
      isScatterPlotTab={activeChartsAndGraphsTab === 3}
      typeOfFrequency={typeOfFrequency()}
    />
  );
};

SettingsFormContainer.propTypes = {
  visibleParameters: PropTypes.object.isRequired,

  firstParameterName: PropTypes.string,
  firstParameterType: PropTypes.string,
  secondParameterName: PropTypes.string,
  secondParameterType: PropTypes.string,

  firstParameterSelected: PropTypes.bool.isRequired,

  onFirstParameterNameChange: PropTypes.func.isRequired,
  onSecondParameterNameChange: PropTypes.func.isRequired,

  numericalName: PropTypes.string.isRequired,

  datasetLength: PropTypes.number.isRequired,
  showHistogramFields: PropTypes.bool,
  activeChartsAndGraphsTab: PropTypes.number.isRequired,

  isFrequencyTable: PropTypes.bool.isRequired,
  isRelativeFrequency: PropTypes.bool,
  frequencyParameters: PropTypes.object,
  frequencyDataSetLength: PropTypes.number,

  typeOfFrequencyHistogram: PropTypes.string.isRequired,
  typeOfFrequencyBarChart: PropTypes.string.isRequired,
  typeOfFrequencyPieChart: PropTypes.string.isRequired,
  setTypeFrequencyHistogram: PropTypes.func.isRequired,
  setTypeFrequencyBarChart: PropTypes.func.isRequired,
  setTypeFrequencyPieChart: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  datasetLength: getFilteredDatasetLength(state),
  visibleParameters: getVisibleSelectedParameters(state),
  activeChartsAndGraphsTab: getActiveChartsAndGraphsTab(state),
  frequencyParameters: getCategoricalSummary(state),
  frequencyDataSetLength: getFrequencyDataSetLength(state),
  typeOfFrequencyHistogram: getTypeOfFrequencyHistogram(state),
  typeOfFrequencyBarChart: getTypeOfFrequencyBarChart(state),
  typeOfFrequencyPieChart: getTypeOfFrequencyPieChart(state),
  isRelativeFrequency: isRelativeFrequencyTable(state)
});

const mapDispatchToProps = {
  setTypeFrequencyPieChart,
  setTypeFrequencyBarChart,
  setTypeFrequencyHistogram
};

export default connect(mapStateToProps, mapDispatchToProps)(SettingsFormContainer);
